import { useCallback, useMemo } from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../redux/store";
import { isMassValidationOk } from "../../utils/typedUtils";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import type { ExtraType, PackType } from "../../../../../common-deprecated/types/CommonTypes";
import { addExtra, addPack, removeExtra, removePack, resetBasket } from "../../redux/actions/BasketActions";
import { startMassManagementFetch } from "../../redux/actions/MassManagementActions";
import { finishTransaction } from "../../../../../common-deprecated/redux/actions/TransactionActions";
import { useDispatch } from "../../../../../shared-logic/redux/store";

export type MassManagementViewType = {
    onCancel: () => void;
    onOk: () => void;
    validates: boolean;
    exceedingKilogram: string;
    originalBasket: BasketType;
    packs: PackType[];
    extras: ExtraType[];
    packClick: (pack: PackType, selected: boolean) => void;
    extraClick: (extra: ExtraType, selected: boolean) => void;
};

const useModalMassManagement = (close: () => void): MassManagementViewType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const basket = useBnbSelector((state) => state.basket);
    const massManagement = useBnbSelector((state) => state.massManagement);
    const { extras, packs } = basket;

    // In both packClick and extraClick dispatches providing the transaction as false will not trigger the post transaction logic dispatches, among them is the automatic
    // loading of the mass management, see TransactionLogic for more info
    // TODO This does not work together when resolving conflicting extras/packs which require a user selection, not sure how relevant this is though. Deselection works just fine.
    // The effect is that in this specific case it will trigger the startMassManagementFetch before the conflicts are resolved
    const packClick = (pack: PackType, selected: boolean) => {
        if (selected) dispatch(removePack(pack, false));
        else dispatch(addPack(pack, [], [], false, false, true, false));
        // Load the mass management
        dispatch(startMassManagementFetch());
    };

    const extraClick = (extra: ExtraType, selected: boolean) => {
        if (selected) dispatch(removeExtra(extra, false, false));
        else dispatch(dispatch(addExtra(extra, false, false)));
        // Load the mass management
        dispatch(startMassManagementFetch());
    };

    // Save original basket value for resetting purposes
    const originalBasket = useMemo(() => basket, []);
    const hasSomethingChanged =
        extras.length !== originalBasket.extras.length || packs.length !== originalBasket.packs.length;
    const onOk = useCallback(() => {
        // Make sure all transaction logic is triggered when closing the modal
        if (hasSomethingChanged) {
            dispatch(finishTransaction());
        }
        close();
    }, [extras, packs]);

    const onCancel = useCallback(() => {
        // Reset the basket to the initial state it was when opening the modal
        // No transaction end logic is triggered so the only thing we need to do is re-initialize the basket
        if (hasSomethingChanged) {
            dispatch(resetBasket(originalBasket));
        }
        close();
    }, [extras, packs]);

    const exceedingKilogram = String(massManagement.exceedingKilogram);
    const validates = isMassValidationOk(massManagement);

    return {
        onCancel,
        onOk,
        validates,
        exceedingKilogram,
        originalBasket,
        packs,
        extras,
        packClick,
        extraClick,
    };
};

export default useModalMassManagement;
